import React, { useEffect, useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from '../TypeAhead';

/**
 * Reusable async lookup field for the tablemaint drawers, built on the shared
 * <TypeAhead> (freeText mode) with rich `renderOption` results — the same pattern
 * as the order-management BillToLookupField / CustomerLookupField (which render a
 * LookupResultCard per option). Typing debounces a server search; picking a row
 * calls `onSelect` with the source item; the dropdown is portaled into the
 * enclosing <dialog> so it isn't hidden behind the drawer's top layer.
 *
 * The stored value can differ from the displayed text (`displayValue`): a carrier
 * stores a recno but shows the carrier name; a template stores/shows the same
 * T-number. `checkStatus` optionally flags a stale stored value in red
 * (e.g. "TEMPLATE DELETED").
 */
interface Props<T> {
  label: string;
  /** Text shown in the input (freeText mirrors it). */
  displayValue: string;
  /** User typed free text (not a pick) — e.g. rename + unlink. */
  onType: (text: string) => void;
  /** User picked a result row. */
  onSelect: (item: T) => void;
  search: (q: string) => Promise<T[]>;
  /** Rich per-option rendering (e.g. a LookupResultCard). */
  renderItem: (item: T) => React.ReactNode;
  itemValue: (item: T) => string; // option value/key
  itemLabel: (item: T) => string; // plain-text fallback label
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  minChars?: number; // default 2
  /** Orange-highlight the input: the stored reference is deleted or no longer
   *  exists. Matches the invalid-value styling the plain selects use. */
  invalid?: boolean;
  /** Stored value to validate + the async check that returns a red status label. */
  statusValue?: string;
  checkStatus?: (value: string) => Promise<string | null>;
  /** Must-pick mode: if the user types (searches) but clicks out WITHOUT picking a
   *  result, the free-typed text is cleared on close. A seeded or selected value is
   *  kept (only text that diverged from the last committed value is dropped). Assumes
   *  the displayed text IS the stored value (e.g. a template #). */
  clearOnBlurWithoutSelect?: boolean;
  /** Suppress the field's own label (for table-style rows where the column header is
   *  rendered once above the rows). */
  hideLabel?: boolean;
}

export function LookupSearchField<T>({
  label, displayValue, onType, onSelect, search, renderItem, itemValue, itemLabel,
  required, disabled, placeholder, minChars = 2, statusValue, checkStatus, invalid,
  clearOnBlurWithoutSelect, hideLabel,
}: Props<T>) {
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Must-pick tracking: `committed` is the last seeded/selected value; `lastEmitted`
  // distinguishes our own onType/onSelect echoes from an external (seed) displayValue.
  const committed = useRef(displayValue);
  const lastEmitted = useRef<string | null>(null);
  useEffect(() => {
    if (displayValue !== lastEmitted.current) committed.current = displayValue; // external seed
  }, [displayValue]);

  const handleType = (text: string) => { lastEmitted.current = text; onType(text); };
  const handleSelect = (item: T) => {
    const v = itemValue(item);
    committed.current = v;
    lastEmitted.current = v;
    onSelect(item);
  };
  const handleClose = () => {
    if (!clearOnBlurWithoutSelect) return;
    if (displayValue.trim() && displayValue.trim() !== committed.current.trim()) {
      lastEmitted.current = '';
      onType(''); // typed but not selected → discard the free text
    }
  };

  // Portal the dropdown into the enclosing <dialog> (top layer) so it isn't clipped.
  useEffect(() => { setPortalEl((wrapRef.current?.closest('dialog') as HTMLElement | null) ?? null); }, []);

  // Stale-value flag (e.g. TEMPLATE DELETED).
  useEffect(() => {
    if (!checkStatus) { setStatusLabel(null); return; }
    const v = (statusValue || '').trim();
    if (!v) { setStatusLabel(null); return; }
    let alive = true;
    checkStatus(v).then((l) => { if (alive) setStatusLabel(l); }).catch(() => { if (alive) setStatusLabel(null); });
    return () => { alive = false; };
  }, [statusValue, checkStatus]);

  const runSearch = (term: string) => {
    if (timer.current) clearTimeout(timer.current);
    const q = term.trim();
    if (q.length < minChars) { setOptions([]); setSearching(false); return; }
    // Mark the search in flight from the FIRST keystroke (debounce included), so
    // the dropdown shows "Searching…" instead of a premature "No results found".
    // A sequence counter drops out-of-order responses from stale keystrokes.
    const seq = ++searchSeq.current;
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const items = await search(q);
        if (seq !== searchSeq.current) return;
        setOptions(items.map((it) => ({ value: itemValue(it), label: itemLabel(it), data: it })));
      } catch {
        if (seq === searchSeq.current) setOptions([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
  };

  return (
    <div ref={wrapRef}>
      {!hideLabel && (
        <label className="block text-sm font-medium text-label mb-1">
          {label}{required && <span className="text-red-500"> *</span>}
          {statusLabel && <span className="ml-2 text-xs font-semibold text-red-600 dark:text-red-400">{statusLabel}</span>}
        </label>
      )}
      <TypeAhead
        ariaLabel={label}
        freeText
        multiple={false}
        value={displayValue}
        options={options}
        onChange={handleType}
        onSearch={runSearch}
        onSelect={(opt) => handleSelect(opt.data as T)}
        onClose={clearOnBlurWithoutSelect ? handleClose : undefined}
        renderOption={(opt) => renderItem(opt.data as T)}
        disabled={disabled}
        placeholder={placeholder ?? 'Type to search…'}
        portalContainer={portalEl}
        className="w-full"
        invalid={invalid}
        searching={searching}
        emptyMessage={displayValue.trim().length < minChars
          ? `Type at least ${minChars} character${minChars === 1 ? '' : 's'} to search`
          : undefined}
      />
    </div>
  );
}

export default LookupSearchField;
